import { clearAllStorage, isPreviewMode, useModulesLibStore, usePdf, useTableStore, useUserStore } from "@innoclouds/shared";
import { PREVIEW_SNAPSHOT_CACHE_PREFIX } from "@innoclouds/ui";
import { useLocalStorage } from "@vueuse/core";
import dayjs from "dayjs";
import i18next from "i18next";
import { storeToRefs } from "pinia";
import type { App } from "vue";
import { createRouter, createWebHistory, type RouteLocationNormalizedGeneric } from "vue-router";
import { connectionPlugin } from "../connectionPlugin";
import { useMainStore } from "../store/modules/main/main";
import getOrgUnitId from "../utils/useOrgUnitId";

const baseUrl = import.meta.env.BASE_URL || "/";

let connectionInstalled = false;

const router = createRouter({
	history: createWebHistory(baseUrl),
	routes: [
		{
			path: "/:orgId/",
			name: "home",
			component: () => import("../views/mainPage/Main.vue"),
			meta: { requiresAuth: false },
		},
		{
			path: "/:orgId/product/:id",
			name: "product",
			component: () => import("../views/cardProductPage/CardProductPage.vue"),
			meta: { requiresAuth: false },
		},
		{
			path: "/:orgId/editProduct/:id/:systemId",
			name: "editProduct",
			component: () => import("../views/cardProductPage/CardProductPage.vue"),
			meta: { requiresAuth: false },
		},
		{
			path: "/:orgId/cart",
			name: "cart",
			component: () => import("../views/cartPage/CartPage.vue"),
			meta: { requiresAuth: false },
		},
		{
			path: "/:orgId/register",
			name: "register",
			component: () => import("../views/registerPage/RegisterPage.vue"),
			meta: { requiresAuth: false },
		},
		{
			path: "/:orgId/reserve",
			name: "reserve",
			component: () => import("@/views/reservePage/reservePage.vue"),
			meta: { requiresAuth: true },
		},
		{
			path: "/:orgId/settings",
			name: "settings",
			component: () => import("../views/settingsPage/SettingsPage.vue"),
			meta: { requiresAuth: true },
		},
		{
			path: "/:orgId/profile",
			name: "profile",
			component: () => import("../views/profilePage/ProfilePage.vue"),
			meta: { requiresAuth: true },
		},
		{
			path: "/:orgId/payment",
			name: "payment",
			component: () => import("../views/paymentPage/PaymentPage.vue"),
			meta: { requiresAuth: false },
		},
		{
			path: "/:orgId/order-ts",
			name: "order-ts",
			component: () => import("../views/tableOrderPage/TableOrderPage.vue"),
			meta: { requiresAuth: false },
		},
		{
			path: "/:orgId/order-ts/:id",
			name: "order-ts-status",
			component: () => import("../views/successOrderPage/SuccessOrderPage.vue"),
			meta: { requiresAuth: false },
		},
		{
			path: "/:orgId/userAgreement",
			name: "userAgreement",
			component: () => import("../views/userAgreementPage/UserAgreementPage.vue"),
			meta: { requiresAuth: false },
		},
		{
			path: "/:orgId/nonworkingTime",
			name: "nonworkingTime",
			component: () => import("../views/nonworkingTimePage/NonworkingTimePage.vue"),
			meta: { requiresAuth: false },
		},
		{
			path: "/:orgId/pointsAddress",
			name: "pointsAddress",
			component: () => import("../views/pointsAddressPage/PointsAddressPage.vue"),
			meta: { requiresAuth: false },
		},
		{
			path: "/:orgId/arrivals",
			name: "arrivals",
			component: () => import("../views/arrivalsPage/ArrivalsPage.vue"),
			meta: { requiresAuth: false },
		},
		{
			path: "/:orgId/arrivals-new",
			name: "arrivals-new",
			component: () => import("../views/arrivalsPageNew/ArrivalsPage.vue"),
			meta: { requiresAuth: false },
		},
		{
			path: "/:orgId/errorPayment",
			name: "errorPayment",
			component: () => import("../views/errorPaymentPage/ErrorPaymentPage.vue"),
			meta: { requiresAuth: false },
		},
		{
			path: "/pdf/:orgId/",
			name: "pdf",
			component: () => import("../views/pdfPage/PdfPage.vue"),
			meta: { requiresAuth: false },
		},
		{
			path: "/:pathMatch(.*)*",
			name: "notFound",
			component: () => import("../views/notFound/NotFound.vue"),
			meta: { requiresAuth: false },
		},
	],
});

/** Логика разовтаризации */
router.beforeEach((to, _from, next) => {
	const modulesLibStore = useModulesLibStore();

	if (to?.query?.logout) {
		if (to?.query?.logout === "device") {
			// @tolgee-key { key: 'notification.another-device-session', ns: 'translation' }
			const notifyAnotherDevice = () => modulesLibStore.showNotification(i18next.t(`notification.another-device-session`));
			if (i18next.isInitialized) notifyAnotherDevice();
			else i18next.on("initialized", notifyAnotherDevice);
		}
		delete to.query.logout;
		next({ name: "home", params: to?.params, query: to?.query });
	} else {
		next();
	}
});

const clearLocalStorage = () => {
	const oldMode = localStorage.getItem("appMode");
	localStorage.clear();
	if (oldMode) localStorage.setItem("appMode", oldMode);
};

export const routerChecked = async (to: RouteLocationNormalizedGeneric, app: App) => {
	const { isDesktop, syncWithRoute } = usePdf();
	if (to?.name === "notFound") return true;
	const version = useLocalStorage<string | null>("version", __APP_VERSION__);

	//Чистка кеша если есть отличие по версиям
	if (version.value !== __APP_VERSION__) {
		await clearAllStorage();

		if ("caches" in window && !isPreviewMode) {
			const cacheNames = await caches.keys();
			await Promise.all(cacheNames.filter((cacheName) => !cacheName.startsWith(PREVIEW_SNAPSHOT_CACHE_PREFIX)).map((cacheName) => caches.delete(cacheName)));
		}
		version.value = __APP_VERSION__;
		if (!isPreviewMode) window.location.reload();
	}

	const userStore = useUserStore();
	const mainStore = useMainStore();
	const tableStore = useTableStore();

	const { cartProducts, typeClearUser, appMode } = storeToRefs(mainStore);

	const orgId = to?.params?.orgId?.toString();
	const tableId = to?.query?.table_id?.toString();

	syncWithRoute(to);

	if (isDesktop.value && to.name !== "notFound") {
		const allowedRoutes = ["home", "product"];
		const isAllowedRoute = allowedRoutes.includes(to.name as string);

		if (!isAllowedRoute) {
			return {
				path: `/${orgId}/`,
				query: { mode: "pdf" },
			};
		}
	}
	if (orgId && tableId) {
		mainStore.takeDataTable(orgId, tableId);
	}

	if (to?.query?.mode && to?.query?.mode !== "pdf") appMode.value = to.query.mode.toString();

	//Чистка локального хранилища при смене точки организации
	if (!orgId || orgId === "undefined" || orgId.trim() === "") return { name: "notFound" };
	try {
		if (localStorage.getItem("orgID")) {
			if (orgId !== localStorage.getItem("orgID")) {
				if (typeClearUser.value.type !== "" && typeClearUser.value.addressId !== "") {
					if (typeClearUser.value.type === "org") {
						clearLocalStorage();
					} else {
						localStorage.removeItem("cart");
						localStorage.removeItem("address");
						localStorage.removeItem("bonus");
						cartProducts.value = [];
						tableStore.id = undefined;
					}
				} else {
					clearLocalStorage();
				}
			}
		}
		await getOrgUnitId(orgId);
		localStorage.setItem("orgID", orgId);
		if (!connectionInstalled) {
			app.use(connectionPlugin);
			connectionInstalled = true;
		}
	} catch (error) {
		console.error("Error in getOrgUnitId:", error);
		return { name: "notFound" };
	}

	if (to?.name !== "notFound") {
		const now = dayjs();
		const startTime = dayjs().set("hour", 8).set("minute", 0).set("second", 0);
		const endTime = dayjs().set("hour", 20).set("minute", 0).set("second", 0);
		const isNonWorkingTime = now.isAfter(startTime) || now.isBefore(endTime);

		if (to?.name !== `nonworkingTime` && !isNonWorkingTime) return { name: `nonworkingTime`, params: { orgId } };
		if (to?.name === `nonworkingTime` && isNonWorkingTime) return { name: `home`, params: { orgId } };
	}

	if (to?.meta?.requiresAuth) {
		if (!userStore.isInitialized) {
			await userStore.initAuth();
		}

		if (!userStore.isAuthorized) {
			return { name: `home`, params: { orgId } };
		}
	}

	// @TODO: Проверить что на истекших токенах все работает корректно, ниже старая логика.
	// const exp = user.value.exp ?? 0;
	// const isExpired = exp === 0 || dayjs.unix(exp).isBefore(dayjs());
	// if (isExpired) {
	// 	authStore.logout();
	// 	return { name: `home`, params: { orgId } };
	// }

	return true;
};

export default router;
